import { useState, type FormEvent } from "react";
import { useSession } from "../hooks";
import { permalink } from "../links";
import type { Message } from "../sync/types";
import { Quote } from "./LinkPreviews";
import { RecipientPicker, sendTo, type To } from "./RecipientPicker";
import { Form, Sheet } from "./ui";
import { list, t } from "../i18n";

// ShareMessage sends a link to a message, with a note, to a channel or
// people. Those who can read the message see it quoted; others, only that
// there is one.
export function ShareMessage({ m, onClose, done }: { m: Message; onClose: () => void; done: (said: string) => void }) {
  const session = useSession();
  const [to, setTo] = useState<To[]>([]);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  async function share(e: FormEvent) {
    e.preventDefault();
    if (!to.length) return;
    setError("");
    setSending(true);
    try {
      const link = permalink(location.origin, m);
      await sendTo(session, to, note.trim() ? `${note.trim()}\n${link}` : link);
      done(t("Shared with {names}", { names: list(to.map((x) => x.label)) }));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSending(false);
    }
  }

  return (
    <Sheet title={t("Share message")} onClose={onClose} className="page share-sheet">
      <Form onSubmit={share}>
        <RecipientPicker to={to} onChange={setTo} autoFocus />
        <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("Add a note (optional)")} aria-label={t("Note")} rows={2} />
        <Quote m={m} />
        <p className="field-hint">{t("Only people who can read the original see it quoted.")}</p>
        {error && <p className="error">{error}</p>}
        <div className="form-actions">
          <button type="button" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button type="submit" disabled={sending || !to.length}>
            {sending ? t("Sharing…") : t("Share")}
          </button>
        </div>
      </Form>
    </Sheet>
  );
}
